/**
 * A part number that opens that part's order lines in a new tab (CR-9120):
 * PO Lines for a customer, Sales Order Line Items for a supplier, both scoped
 * by the server to the viewer's own lines and landing on the Open tab.
 *
 * Shared by Stock Planning (CR-6176), the call-off's lines, and the call-off
 * wizard's warehouse and direct delivery grids, so every part number that
 * links goes to the same place.
 */
import type { MouseEvent } from 'react';
import { Button } from '../../ui/button';

/**
 * Opens the order lines for one part in a new tab, so the user can compare
 * them with the screen they came from side by side. PO Lines reads
 * `partNumber` from the address on mount (usePOLinesState); a fresh tab
 * recovers the session through the CR-6282 BroadcastChannel hand-off.
 *
 * @param partNumber - the part to filter the order lines to
 */
export function openPartOrderLines(partNumber: string): void {
  const params = new URLSearchParams({ tab: 'po_lines', partNumber });
  window.open(`/purchasing?${params.toString()}`, '_blank', 'noopener,noreferrer');
}

interface PartNumberLinkProps {
  partNumber: string;
}

/**
 * The part number as a link to its order lines; a blank one stays plain text
 * rather than a link to nothing.
 *
 * @param partNumber - the part number to show and link
 * @returns the link, or the bare text when there is no part number
 */
export default function PartNumberLink({ partNumber }: PartNumberLinkProps) {
  if (!partNumber.trim()) return <>{partNumber}</>;
  const open = (e: MouseEvent<HTMLButtonElement>) => {
    // The grids it sits in select or expand rows on click; this click is
    // for the link alone.
    e.stopPropagation();
    openPartOrderLines(partNumber);
  };
  return (
    <Button
      type="button"
      variant="link"
      className="h-auto p-0 font-mono text-xs font-medium"
      title="Open this part's order lines in a new tab"
      onClick={open}
    >
      {partNumber}
    </Button>
  );
}
